<script setup lang="ts">
import { computed } from "vue";
import { _battle } from "../ui/battle";
import { Prop } from "../core/prop";
import { Seer } from "../core/seer";
import { Side } from "../core/round";

const battle = _battle;
const props = defineProps<{ seer: Seer; side: Side }>();

const actionBlocked = computed(
    () => {
        if (battle.phase !== "selecting" || battle.viewMode !== "live") {
            return true;
        }
        if (battle.needsReplacement(props.side)) {
            return !battle.getReplacement(props.side);
        }
        return !props.seer.getBattlePet()?.isAlive();
    }
);

function isPending(item: Prop): boolean {
    const pending = battle.getPending(props.side);
    return (
        battle.hasSelected(props.side) &&
        pending?.kind === "prop" &&
        pending.propType === item.type
    );
}

function usesLevel(cur: number, max: number): string {
    const ratio = max > 0 ? cur / max : 0;
    if (ratio > 0.5) return "uses-high";
    if (ratio > 0.25) return "uses-medium";
    return "uses-low";
}
</script>

<template>
    <div class="prop-group">
        <button
            v-for="item in seer.propPackage"
            :key="item.type"
            class="prop-btn"
            :class="{
                selected: isPending(item),
                depleted: item.curUses <= 0,
            }"
            :disabled="actionBlocked || item.curUses <= 0"
            :title="`${item.name}（剩余 ${item.curUses}/${item.maxUses} 次）`"
            @click="battle.selectOperation(side, { kind: 'prop', propType: item.type })"
        >
            <img class="prop-icon" :src="item.iconUrl" alt="" />
            <span class="prop-name">{{ item.name }}</span>
            <div
                class="uses-bars"
                :class="usesLevel(item.curUses, item.maxUses)"
            >
                <span
                    v-for="i in item.maxUses"
                    :key="i"
                    class="uses-cell"
                    :class="{ filled: i <= item.curUses }"
                ></span>
            </div>
        </button>
    </div>
</template>

<style scoped>
.prop-group {
    display: inline-flex;
    gap: calc(var(--ui-unit) * 8);
}

.prop-btn {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    gap: calc(var(--ui-unit) * 5);
    width: calc(var(--ui-unit) * 82);
    padding: calc(var(--ui-unit) * 6) calc(var(--ui-unit) * 4);
    background: rgba(30, 30, 45, 0.9);
    border: calc(var(--ui-unit) * 1) solid rgba(255, 255, 255, 0.08);
    border-radius: calc(var(--ui-unit) * 8);
    cursor: pointer;
    transition: all 0.2s ease;
    backdrop-filter: blur(calc(var(--ui-unit) * 6));
}

.prop-btn:hover:not(:disabled) {
    background: rgba(40, 40, 60, 0.95);
    border-color: rgba(255, 215, 0, 0.35);
    transform: translateY(calc(var(--ui-unit) * -2));
    box-shadow: 0 calc(var(--ui-unit) * 4) calc(var(--ui-unit) * 16) rgba(255, 215, 0, 0.12);
}

.prop-btn.selected {
    border-color: #ffd700;
    background: rgba(255, 215, 0, 0.08);
    box-shadow: 0 0 calc(var(--ui-unit) * 16) rgba(255, 215, 0, 0.2);
    transform: translateY(calc(var(--ui-unit) * -2));
}

.prop-btn:disabled,
.prop-btn.depleted {
    cursor: not-allowed;
    opacity: 0.4;
    transform: none;
}

.prop-icon {
    width: calc(var(--ui-unit) * 28);
    height: calc(var(--ui-unit) * 28);
    object-fit: contain;
}

.prop-name {
    max-width: 100%;
    overflow: hidden;
    color: #e0e0e0;
    font-size: calc(var(--ui-unit) * 12);
    font-weight: 600;
    line-height: 1.1;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.uses-bars {
    display: flex;
    width: 100%;
    height: calc(var(--ui-unit) * 4);
    gap: calc(var(--ui-unit) * 1);
    margin-top: calc(var(--ui-unit) * 2);
}

.uses-cell {
    flex: 1 1 0;
    min-width: 0;
    height: 100%;
    border-radius: calc(var(--ui-unit) * 1);
    background: rgba(255, 255, 255, 0.14);
}

.uses-bars.uses-high .uses-cell.filled {
    background: #22c55e;
    box-shadow: 0 0 calc(var(--ui-unit) * 3) #22c55e;
}

.uses-bars.uses-medium .uses-cell.filled {
    background: #eab308;
    box-shadow: 0 0 calc(var(--ui-unit) * 3) #eab308;
}

.uses-bars.uses-low .uses-cell.filled {
    background: #ef4444;
    box-shadow: 0 0 calc(var(--ui-unit) * 3) #ef4444;
}
</style>
